import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';

import FileDropField from '../../../shared/components/FileDropField';
import { RADIUS, SHADOWS } from '../../../theme/muiTheme';

export function SourceIcon({ icon: Icon, tone }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: alpha(tone.color, theme.palette.mode === 'dark' ? 0.13 : 0.075),
        border: 1,
        borderColor: alpha(tone.color, theme.palette.mode === 'dark' ? 0.24 : 0.13),
        borderRadius: `${RADIUS.control}px`,
        color: tone.color,
        display: 'flex',
        flex: '0 0 auto',
        height: 40,
        justifyContent: 'center',
        width: 40,
      }}
    >
      <Icon fontSize="small" />
    </Box>
  );
}

export function HistoryButton({ onClick }) {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('dataInput.actions.history')}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={t('dataInput.actions.history')}
        sx={{ border: 1, borderColor: 'divider', borderRadius: `${RADIUS.control}px` }}
      >
        <HistoryRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

export function DataSourceRow({
  title,
  description,
  icon,
  tone,
  actions,
  children,
  muted = false,
}) {
  return (
    <Box
      sx={(theme) => ({
        bgcolor: muted
          ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.012)
          : 'background.paper',
        border: 1,
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderRadius: `${RADIUS.control}px`,
        boxShadow: theme.palette.mode === 'dark' ? 'none' : SHADOWS.e1,
        p: { xs: 1.5, sm: 2 },
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
          <SourceIcon icon={icon} tone={tone} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 750 }}>
              {title}
            </Typography>
            {description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                {description}
              </Typography>
            )}
            {children && (
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.75,
                  mt: 0.75,
                  '& > .MuiTypography-root': { flexBasis: '100%' },
                }}
              >
                {children}
              </Box>
            )}
          </Box>
        </Stack>
        {actions && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, flexShrink: 0 }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default function UploadSourceCard({
  title,
  description,
  icon,
  tone,
  source,
  pendingFile,
  onFileChange,
  onUpload,
  onViewProgress,
  onViewResult,
  viewResultDisabled = false,
  onDownload,
  onTemplateDownload,
  onHistory,
  onDelete,
  busy = false,
  disabled = false,
  canUpload = true,
  downloadDisabled = false,
  templateDownloading = false,
  templateDownloadDisabled = false,
  fileError,
  fillContent = false,
}) {
  const { t } = useTranslation();
  const currentMeta = source.currentMetaKey
    ? t(source.currentMetaKey, source.currentMetaParams)
    : source.currentMeta;

  return (
    <Box
      sx={(theme) => ({
        bgcolor: 'background.paper',
        border: 1,
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderRadius: `${RADIUS.control}px`,
        boxShadow: theme.palette.mode === 'dark' ? 'none' : SHADOWS.e1,
        overflow: 'hidden',
        ...(fillContent && {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
        }),
      })}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 }, flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <SourceIcon icon={icon} tone={tone} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 750 }}>{title}</Typography>
              {!source.currentName && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  {description}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, flexShrink: 0 }}
          >
            {(canUpload || busy) && (
              <Button
                variant="outlined"
                size="small"
                startIcon={busy
                  ? <CircularProgress size={14} role="presentation" aria-hidden="true" />
                  : <UploadFileRoundedIcon />}
                onClick={busy ? onViewProgress : onUpload}
                disabled={busy ? !onViewProgress : (disabled || !pendingFile)}
              >
                {busy ? t('dataInput.actions.processing') : t('dataInput.actions.upload')}
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteOutlineRoundedIcon />}
                onClick={onDelete}
                disabled={disabled || busy}
              >
                {t('dataInput.actions.delete')}
              </Button>
            )}
            {onViewResult && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRoundedIcon />}
                onClick={onViewResult}
                disabled={viewResultDisabled || busy}
              >
                {t('dataInput.actions.viewResult')}
              </Button>
            )}
            <HistoryButton onClick={onHistory} />
          </Stack>
        </Stack>
      </Box>
      {canUpload && (
        <Box
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.012),
            borderTop: 1,
            borderColor: 'divider',
            p: { xs: 1.5, sm: 2 },
            ...(fillContent && {
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
            }),
          })}
        >
          {onTemplateDownload && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.5, flexShrink: 0 }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('dataInput.template.helper')}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={templateDownloading
                  ? <CircularProgress size={14} role="presentation" aria-hidden="true" />
                  : <DownloadRoundedIcon />}
                onClick={onTemplateDownload}
                disabled={templateDownloading || templateDownloadDisabled}
                sx={{ flexShrink: 0 }}
              >
                {t(templateDownloading
                  ? 'dataInput.template.downloading'
                  : 'dataInput.template.download')}
              </Button>
            </Stack>
          )}
          <Box
            sx={{
              pointerEvents: disabled ? 'none' : 'auto',
              opacity: disabled ? 0.58 : 1,
              ...(fillContent && {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                minHeight: 0,
              }),
            }}
          >
            <FileDropField
              file={pendingFile}
              currentName={source.currentName}
              currentMeta={currentMeta}
              onChange={onFileChange}
              accept={'.xlsx'}
              maxSizeMb={25}
              acceptLabel={t('dataInput.file.accepted', { max: 25 })}
              storedLabel={t('dataInput.file.latestSuccessful')}
              pendingUploadLabel={t('dataInput.file.pendingUpload')}
              onStoredFileClick={onDownload}
              storedFileActionLabel={source.currentName
                ? t('dataInput.history.downloadFile', { file: source.currentName })
                : undefined}
              storedFileDisabled={downloadDisabled}
              externalError={fileError}
              fillHeight={fillContent}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
